/*
 * XNGAGE CONFIDENTIAL
 * __________________________
 *
 * Copyright (C) 2020 Xngage - All Rights Reserved
 *
 * All code or information contained herein is, and remains the
 * property of Xngage LLC and its customers. The intellectual
 * and technical concepts contained are proprietary to Xngage LLC
 * and may be covered by U.S. and Foreign Patents, patents in
 * process, and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Xngage LLC.
 */

import { browserUnsupportedCss } from "@eisinc/App/Components/Common/BrowserUnsupportedWarning";
import { PageStyle } from "@eisinc/MobiusOverrides/Page/Page";
import { HEADER_BREAKPOINT } from "@eisinc/Overrides/Pages/Header";
import { PageLayoutWrapper } from "@eisinc/Overrides/PageLayout";
import { fontConfig } from "@eisinc/XDL/PreStyle/typography";
import { xBaseTheme } from "@eisinc/XDL/types";
import { xScreen } from "@eisinc/XDL/Utilities";
import { LoadingSpinnerStyle } from "@insite/mobius/LoadingSpinner";
import getColor from "@insite/mobius/utilities/getColor";
import { css } from "styled-components";

export default {
    css: css`
        body {
            &:after {
                transition: background-color 600ms ease;
                content: "";
                position: fixed;
                top: 0;
                left: 0;
                z-index: 9;
                width: 100%;
                height: 100%;
                pointer-events: none;
            }
        }

        div[data-test-selector="page_ProductListPage"] div[class^="PageLayoutWrapper"] > main,
        div[data-test-selector="page_CategoryDetailsPage"] div[class^="PageLayoutWrapper"] > main,
        div[data-test-selector="page_ProductDetailsPage"] div[class^="PageLayoutWrapper"] > main {
            min-height: 85vh;
            > div {
                ${LoadingSpinnerStyle} {
                    display: none;
                }
            }
        }

        div[data-test-selector="page_ProductListPage"] ${PageLayoutWrapper} > main > div {
            div:first-child {
                ${LoadingSpinnerStyle} {
                    display: block;
                    height: 10px; /* XNG-Change: height for CategoryName */
                }
            }
            div:nth-child(2) {
                ${LoadingSpinnerStyle} {
                    display: block;
                    height: 160px; /* XNG-Change: height For CategoryDetailsBanner */
                    ${xScreen("lte", "md")} {
                        height: 340px;
                    }
                }
            }
        }

        ${PageStyle}:has(> [data-test-selector="pageBreadcrumbs"]) {
            padding-top: 0;
            padding-bottom: 0;
        }

        @media print {
            [class^="LoadingOverlayStyle"] {
                display: block !important;
            }
            #onetrust-consent-sdk {
                display: none;
            }
        }

        body.shade {
            &:after {
                background: ${getColor("common.black")};
                opacity: 0.5;
            }
        }

        .qr-scanner {
            [class^="ContentBody"] {
                top: 0;
            }

            &.global-site-message-found [class^="ContentBody"] {
                top: 0;
            }
        }

        *:focus {
            outline: none !important;
        }

        @media print {
            #chatButton {
                display: none;
            }

            [class^="PageStyle"] {
                padding: 0 !important;
            }

            [class^="Scrim"] .drawer [class^="ContentBody"] {
                left: -10px;
            }
        }

        ${xScreen("lte", HEADER_BREAKPOINT)} {
            body {
                padding-top: var(--header-height, 164px);
                @media print {
                    padding-top: 10px;
                }
            }
        }

        .small {
            font-size: ${fontConfig.singleLineSmall.size}px;
            line-height: ${fontConfig.singleLineSmall.lineHeight};
        }

        .xSmall {
            font-size: ${fontConfig.singleLinexSmall.size}px;
            line-height: ${fontConfig.singleLinexSmall.lineHeight};
        }

        .weight-regular {
            font-weight: ${fontConfig.weight.regular};
        }

        .weight-medium {
            font-weight: ${fontConfig.weight.medium};
        }

        .mb-8 {
            margin-bottom: 8px;
        }

        .mb-12 {
            margin-bottom: 12px;
        }

        // 165 + 40 margin (OOTB) = 205px, the height of the header
        div[class^="PageLayoutWrapper"] > div:first-child ${LoadingSpinnerStyle} {
            height: 165px;

            ${xScreen("lte", "md")} {
                display: none; /* XNG-Change: hide the loading spinner on mobile (because header is sticky) */
            }
        }

        .shipping-tbd-row {
            display: none;
        }

        body.vwo-shipping-row .shipping-tbd-row {
            display: flex;
        }

        ${browserUnsupportedCss}
    `,
} as xBaseTheme["globalStyle"];
